import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import AssignDialog from "@/components/AssignDialog";
import { VisualObjects } from "@/components/VisualObjects";
import { HazmatSdbStatusBanner } from "@/components/HazmatSdbStatusBanner";
// NichtAusSdbText: satzweises `[Nicht aus SDB]`-Badge-Rendering (MP-3.7).
import { NichtAusSdbText } from "@/components/HazmatSdbStatusBanner";
import { RichtigFalschRenderer } from "@/vsi/renderers";
import { buildRichtigFalsch } from "@/vsi/richtigFalsch";
import ZweiTuerenPopup, {
  buildVorschauContent,
  sollZweiTuerenPopupOeffnen,
} from "@/components/landing/ZweiTuerenPopup";
import ChrisReadinessActivity from "@/components/landing/chris/ChrisReadinessActivity";
import {
  setActiveLehrbildWorld,
  setLehrbildDesktopKontext,
  setLehrbildPrintKontext,
  useLehrbildImage,
  _debugGetManifestMeta,
} from "@/vsi/lehrbild";
import {
  AccountPolicyBanner,
  usePolicyBanner,
} from "@/components/AccountPolicyBanner";
import { getAccountPolicyMessage } from "@/lib/accountPolicy";
import { useOwnerSession } from "@/hooks/useOwnerSession";
import { toast } from "sonner";
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  Plus,
  ShieldAlert,
  FileWarning,
  BookOpen,
  HelpCircle,
  Wrench,
  RotateCw,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  MapPin,
  Pencil,
  Trash2,
  Check,
  X,
  Eye,
  Printer,
  Lightbulb,
  Package,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { safeAutoFocusRef } from "@/lib/safeAutoFocus";

// Anker C (ADR-000 v1.4 / ROADMAP v2 Phase 2):
// Eine einzige Eingabe -> komplette Dokumentationswelt.
// Diese Seite zeigt die 6 Bausteine zusammen, mit Freigabe-Workflow.

const SUBJECT_TYPE_LABEL = {
  machine: "Maschine",
  hazmat: "Gefahrstoff",
  vehicle: "Fahrzeug",
  activity: "Tätigkeit",
};

const RECURRENCE_LABEL = {
  einmalig: "Einmalig",
  monatlich: "Monatlich",
  vierteljaehrlich: "Vierteljährlich",
  halbjaehrlich: "Halbjährlich",
  jaehrlich: "Jährlich",
  zwei_jahre: "Alle 2 Jahre",
};

const RESIDUAL_RISK_COLOR = {
  gering: "bg-emerald-50 text-emerald-800 border-emerald-200",
  mittel: "bg-amber-50 text-amber-800 border-amber-200",
  hoch: "bg-rose-50 text-rose-800 border-rose-200",
};

// DEMO-SAFE-2 (Juni 2026): Anzeige-Reihenfolge der vorhandenen Visual Learning
// Objects im Dokumentationsbereich. Rein informativ; verändert keine Inhalte,
// Nachweise oder PDFs. Nur bereits vorhandene VLO-Typen, keine neuen.
// IMPLEMENT-6 / S5: learning_focus zusätzlich in der Unternehmer-Weltansicht
// aktiviert, didaktisch zuerst (vor Gefahren-/PSA-Markern und Sicherheitszeichen).
// Fail-closed: fehlt/leer/ungültig -> Renderer liefert null, der Eintrag entfällt;
// übrige VLOs bleiben unverändert. Nur Frontend-Anzeige, keine Beweis-/PDF-Wirkung.
const VLO_DISPLAY_ORDER = { learning_focus: 1, hazard_markers: 2, psa_summary: 3, safety_symbols: 4 };

function formatDate(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function SectionCard({ icon: Icon, title, subtitle, testId, children }) {
  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid={testId}
    >
      <div className="px-6 py-4 border-b border-slate-100 flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </Card>
  );
}

// P0-WAITING-UX (2026-06) → CHRIS Phase C1 (2026-06):
// Das frühere dekorative CLARTEXT-Strichmännchen (CSS-only auf einem Ladebalken,
// animierte layoutbasiert `left: calc(...)` → Chrome-Freeze) wurde vollständig
// durch Chris ersetzt (siehe ChrisReadinessActivity + chrisWelt.css). Chris
// bildet den echten Readiness-Zustand ab (Pose 22/20/23), rein dekorativ.

// P0-D3 (2026-07-16) — Readiness-Ampel für den Freigabe-Bereich.
// REINE Anzeige der bereits klassifizierten Backend-Wahrheit
// (`/api/courses/{id}/release-readiness`). Farben ausschließlich aus
// `readiness.status`. KEINE Interpretation von `code`, `rule` oder
// `message`; alle Backendtexte bleiben vollständig verfügbar. Netzwerk-/
// Serverfehler bleiben neutral (kein grün, kein fachliches `blocked`).
//
// P0-WAITING-UX (2026-06):
//  • Im normalen Running-Wartezustand gibt es KEINEN manuellen
//    „Status aktualisieren"-Button mehr — die Aktualisierung erfolgt
//    automatisch (terminale Manifest-Kante in Welt()).
//  • Manueller Retry („Erneut prüfen") existiert AUSSCHLIESSLICH im echten
//    technischen Fehlerzustand (Readiness-Request fehlgeschlagen).
//  • V3/V4/V5-Diagnosetexte sind im Running-Zustand standardmäßig
//    eingeklappt („Technische Details anzeigen") — unverändert und
//    vollständig, nur sekundär dargestellt. Bei `blocked` bleiben sie
//    prominent (fachlich notwendige Handlungsinformation).
function ReadinessAmpel({ readiness, loading, error, onRefresh }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const status = readiness?.status;
  let dotClass = "bg-slate-300";
  let headline = "Freigabestatus wird geprüft …";
  let testId = "welt-readiness-loading";
  if (loading && !readiness) {
    dotClass = "bg-slate-300 animate-pulse";
    headline = "Freigabestatus wird geprüft …";
    testId = "welt-readiness-loading";
  } else if (error) {
    dotClass = "bg-slate-300";
    headline = "Der Freigabestatus konnte nicht geprüft werden.";
    testId = "welt-readiness-error";
  } else if (status === "ready") {
    dotClass = "bg-emerald-500";
    headline = "Die Welt ist vollständig und kann freigegeben werden.";
    testId = "welt-readiness-ready";
  } else if (status === "running") {
    dotClass = "bg-amber-500";
    headline = "Deine Dokumentationswelt wird gerade fertiggestellt.";
    testId = "welt-readiness-running";
  } else if (status === "blocked") {
    dotClass = "bg-red-500";
    headline = "Die Welt kann noch nicht freigegeben werden.";
    testId = "welt-readiness-blocked";
  } else {
    // Vor dem ersten geladenen Zustand: neutraler Hinweis. Kein grün.
    dotClass = "bg-slate-300";
    headline = "Freigabestatus wird geprüft …";
    testId = "welt-readiness-loading";
  }
  const isRunning = !error && status === "running";
  // CHRIS Phase C1: Chris spiegelt AUSSCHLIESSLICH den echten Readiness-Zustand
  // (running/blocked/ready) aus dem Backend. Bei loading/error erscheint Chris
  // NICHT (kein grüner/fertiger Eindruck ohne autoritatives Signal).
  const chrisStatus =
    !error && (status === "running" || status === "blocked" || status === "ready")
      ? status
      : null;
  const violations = Array.isArray(readiness?.violations) ? readiness.violations : [];
  let checkedText = null;
  if (readiness?.checked_at) {
    try {
      checkedText = new Date(readiness.checked_at).toLocaleString("de-DE");
    } catch {
      checkedText = readiness.checked_at;
    }
  }
  return (
    <div
      className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
      data-testid="welt-readiness-block"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass}`}
          data-testid="welt-readiness-dot"
          data-status={status || (error ? "error" : "loading")}
        />
        <span className="text-slate-800" data-testid={testId}>
          {headline}
        </span>
        {error && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="ml-auto h-7 px-2 text-xs text-slate-600 hover:text-slate-900"
            data-testid="welt-readiness-refresh"
          >
            <RotateCw
              className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`}
            />
            Erneut prüfen
          </Button>
        )}
      </div>
      {isRunning && (
        <p
          className="mt-1 text-xs text-slate-500"
          data-testid="welt-readiness-subtext"
        >
          Bilder, Dokumente und Freigabeprüfung werden automatisch
          vorbereitet.
        </p>
      )}
      {chrisStatus && <ChrisReadinessActivity status={chrisStatus} />}
      {isRunning && violations.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setDetailsOpen((o) => !o)}
            className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700"
            data-testid="welt-readiness-details-toggle"
          >
            {detailsOpen
              ? "Technische Details ausblenden"
              : "Technische Details anzeigen"}
          </button>
        </div>
      )}
      {violations.length > 0 && (!isRunning || detailsOpen) && (
        <ul
          className="mt-2 space-y-1 text-slate-600 list-disc list-inside"
          data-testid="welt-readiness-violations"
        >
          {violations.map((v, i) => (
            <li key={i} data-testid={`welt-readiness-violation-${i}`}>
              {v.message}
            </li>
          ))}
        </ul>
      )}
      {checkedText && (
        <div
          className="mt-1 text-xs text-slate-500"
          data-testid="welt-readiness-checked-at"
        >
          Stand: {checkedText}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Einzelkauf-Anhangseiten (Juni 2026, Betreiberauftrag): Unterweisungsnachweis
// (fest 6 Zeilen, 1:1 aus dem V4H-Muster in UnterweisungsnachweisDruck.jsx,
// Abschnitte 10–12) + Wirksamkeitskontroll-Checkliste. NUR in der
// druckansicht (Chromium-Renderweg) — kein zweiter Renderaufruf, kein
// Backend-Eingriff (test_v4d_2-Vertrag bleibt byte-identisch).
// ---------------------------------------------------------------------------

const DRUCK_NACHWEIS_DOKUMENT_VERSION = "1.0";
const DRUCK_MITARBEITER_ZEILEN = 6;
const DRUCK_WK_MAX_PUNKTE = 5;

// Spiegel der reinen Backend-Funktion extract_observation_points
// (services/wirksamkeit_utils.py): trim, case-insensitive Dedupe, stabile
// Quellreihenfolge (BA-Schutzmaßnahmen → GBU technisch → organisatorisch →
// persönlich), maximal 5 Punkte. Keine neue Datenquelle, keine Erfindung.
function druckBeobachtungspunkte(course) {
  const points = [];
  const seen = new Set();
  const add = (value) => {
    if (points.length >= DRUCK_WK_MAX_PUNKTE) return;
    if (typeof value !== "string") return;
    const text = value.trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    points.push(text);
  };
  const ba = course?.betriebsanweisung;
  for (const pm of (ba && ba.protective_measures) || []) add(pm);
  const hazards = (course?.gbu && course.gbu.hazards) || [];
  for (const field of ["measures_technical", "measures_organizational", "measures_personal"]) {
    for (const hz of hazards) {
      if (hz && typeof hz === "object") add(hz[field]);
    }
  }
  return points;
}

// 1:1-Kopie der UnterschriftTabelle aus UnterweisungsnachweisDruck.jsx (V4H) —
// bewusst lokal, das Original im abgeklemmten Teilbau bleibt unangetastet.
function DruckUnterschriftTabelle({ kopf1, zeilen, testIdPrefix }) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th className="text-left font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 w-[44%]">
            {kopf1}
          </th>
          <th className="text-left font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 w-[22%]">
            Datum
          </th>
          <th className="text-left font-semibold text-slate-900 border-b-2 border-slate-900 pb-2 w-[34%]">
            Unterschrift
          </th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: zeilen }, (_, i) => (
          <tr
            key={i}
            data-testid={
              zeilen === 1
                ? `${testIdPrefix}-zeile`
                : `${testIdPrefix}-zeile-${i + 1}`
            }
          >
            <td className="border-b border-slate-300 h-10">&nbsp;</td>
            <td className="border-b border-slate-300 h-10">&nbsp;</td>
            <td className="border-b border-slate-300 h-10">&nbsp;</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DruckAnkreuzfeld({ label }) {
  return (
    <span className="inline-flex items-center gap-1.5 mr-6 text-sm text-slate-700">
      <span className="inline-block w-3.5 h-3.5 border-2 border-slate-500 rounded-[2px] align-middle" />
      {label}
    </span>
  );
}

// Abschnitte 10–12 des V4H-Musters (Titel, Hinweissatz, 6-Zeilen-Tabelle,
// Unterweisender, Fußbereich) — erscheint IMMER an jedem Einzelkauf-Download.
function DruckNachweisSeite({ course, firmenname }) {
  const erstellungsdatum = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return (
    <section
      className="welt-druck-anhangseite"
      data-testid="welt-druck-nachweisseite"
    >
      <h2
        className="font-heading text-2xl font-bold text-slate-900"
        data-testid="welt-druck-nachweis-titel"
      >
        Unterweisungsnachweis
      </h2>
      <div className="text-sm text-slate-700 mt-2" data-testid="welt-druck-nachweis-thema">
        {course.title || ""}
      </div>
      <div className="text-sm text-slate-600 mt-1" data-testid="welt-druck-nachweis-datum">
        Erstellungsdatum: {erstellungsdatum}
      </div>
      {firmenname ? (
        <div
          className="text-sm text-slate-700 mt-1"
          data-testid="welt-druck-nachweis-firmenname"
        >
          {firmenname}
        </div>
      ) : null}
      <div className="border-t-2 border-slate-900 mt-4" />
      <p className="text-sm text-slate-700 mt-5" data-testid="welt-druck-nachweis-hinweis">
        Die Unterweisung wurde anhand der vorstehenden Unterlagen durchgeführt.
      </p>
      <div className="mt-6" data-testid="welt-druck-nachweis-unterschriften">
        <DruckUnterschriftTabelle
          kopf1="Name, Vorname"
          zeilen={DRUCK_MITARBEITER_ZEILEN}
          testIdPrefix="welt-druck-mitarbeiter"
        />
      </div>
      <div className="mt-8" data-testid="welt-druck-nachweis-unterweisender">
        <DruckUnterschriftTabelle
          kopf1="Name Unterweisender"
          zeilen={1}
          testIdPrefix="welt-druck-unterweisender"
        />
      </div>
      <footer
        className="mt-10 pt-4 border-t border-slate-200"
        data-testid="welt-druck-nachweis-fussbereich"
      >
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Erstellt mit CLartext</span>
          <img
            src="/brand/clartext-logo-full-light.svg"
            alt="CLartext"
            className="h-5 w-auto"
          />
          <span>clartext.io</span>
        </div>
        <div className="flex items-center gap-4 text-[10px] text-slate-400 mt-2">
          <span>Dokumentversion {DRUCK_NACHWEIS_DOKUMENT_VERSION}</span>
          <span>Erstellungsdatum: {erstellungsdatum}</span>
        </div>
      </footer>
    </section>
  );
}

// Kostenlose Papier-Checkliste zur Wirksamkeitskontrolle (Betreiberauftrag
// Teil 2): dieselben deterministischen Beobachtungspunkte wie die digitale
// Abo-Funktion, mit dem festen 28-Tage-Standard als Hinweistext.
function DruckWirksamkeitsChecklisteSeite({ course }) {
  const punkte = druckBeobachtungspunkte(course);
  if (punkte.length === 0) return null;
  const turnusLabel = RECURRENCE_LABEL[course.recurrence] || course.recurrence || "";
  return (
    <section
      className="welt-druck-anhangseite"
      data-testid="welt-druck-wirksamkeitsseite"
    >
      <h2
        className="font-heading text-2xl font-bold text-slate-900"
        data-testid="welt-druck-wk-titel"
      >
        Wirksamkeitskontrolle
      </h2>
      <div className="text-sm text-slate-700 mt-2" data-testid="welt-druck-wk-thema">
        {course.title || ""}
      </div>
      <div className="border-t-2 border-slate-900 mt-4" />
      <p className="text-sm text-slate-700 mt-5" data-testid="welt-druck-wk-hinweis">
        Empfohlene erste Prüfung: ca. 4 Wochen nach der Unterweisung.
        {turnusLabel ? ` Wiederholungsturnus der Unterweisung: ${turnusLabel}.` : ""}
      </p>
      <p className="text-sm text-slate-600 mt-2">
        Prüfen Sie im Arbeitsalltag, ob die folgenden Maßnahmen eingehalten
        werden, und dokumentieren Sie das Ergebnis.
      </p>
      <div className="mt-6 space-y-5">
        {punkte.map((punkt, i) => (
          <div
            key={i}
            className="rounded-md border border-slate-300 p-4"
            data-testid={`welt-druck-wk-punkt-${i + 1}`}
          >
            <p className="text-sm font-medium text-slate-900">
              {i + 1}. {punkt}
            </p>
            <div className="mt-3 text-sm text-slate-700">
              Wird diese Maßnahme im Alltag eingehalten?
            </div>
            <div className="mt-2">
              <DruckAnkreuzfeld label="Ja" />
              <DruckAnkreuzfeld label="Nein" />
              <DruckAnkreuzfeld label="Teilweise" />
            </div>
            <div className="flex gap-8 mt-5">
              <div className="w-[30%]">
                <div className="border-b border-slate-400 h-7" />
                <div className="text-[11px] text-slate-500 mt-1">Datum</div>
              </div>
              <div className="w-[45%]">
                <div className="border-b border-slate-400 h-7" />
                <div className="text-[11px] text-slate-500 mt-1">
                  Unterschrift des Prüfenden
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Welt({ druckansicht = false }) {
  const { id, token, attempt } = useParams();
  const navigate = useNavigate();
  // STUFE 6 (Juli 2026): Anonymer Landingpage-/Preview-Flow (Mandant
  // "public-preview") — interne Verwaltungselemente werden nur ausgeblendet.
  // STUFE 8H: Die Druckansicht blendet dieselben internen Elemente aus.
  const { session: ownerSession } = useOwnerSession();
  const istOeffentlicheVorschau =
    ownerSession?.tenant_id === "public-preview" || druckansicht;
  // ADS-AUTOPREVIEW 01 · Stufe 4.3A: Plakat-Knopf nur bei Vollzugang laut zentralem Zugriffszustand (fail-closed).
  const { company } = useOutletContext() || {};
  const plakatErlaubt =
    !istOeffentlicheVorschau && company?.access_state?.read_only === false;
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();
  // Für Dialog-Flows (neue Version / verwerfen) zusätzlich ein kurzer Toast mit
  // dem freundlichen Titel, da das Seiten-Banner hinter dem Dialog liegt.
  const handlePolicyWithToast = (e) => {
    if (!handlePolicyError(e)) return false;
    const m = getAccountPolicyMessage(e);
    if (m?.title) toast.error(m.title);
    return true;
  };
  const [einsatzbereiche, setEinsatzbereiche] = useState([]);
  const [ebDialogOpen, setEbDialogOpen] = useState(false);
  // Inline-Edit-Modus für Titel + Kurzbeschreibung (nur vor Freigabe verfügbar).
  const [editingHeader, setEditingHeader] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [savingHeader, setSavingHeader] = useState(false);
  // Verwerfen-Dialog (gleiches Backend wie Bibliothek-Löschen).
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  // WELT-ARCHIVIERUNG (Feb 2026, F1–F5): Confirm-Dialog vor dem Archivieren
  // freigegebener Welten. F5-Datenzeile zeigt die konkrete Zahl offener
  // Zuweisungen (Frontend nutzt `listAssignments`, kein neuer GET-Endpoint
  // — spart Code und ist einheitlich mit dem bestehenden Datenmuster).
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveAssignmentStats, setArchiveAssignmentStats] = useState(null);
  // Sprint C (Feb 2026): Versions-Bump-Dialog für bereits freigegebene Welten.
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [nvTitleDraft, setNvTitleDraft] = useState("");
  const [nvDescDraft, setNvDescDraft] = useState("");
  const [nvSubmitting, setNvSubmitting] = useState(false);
  const [newVersionResult, setNewVersionResult] = useState(null);
  // P1.1 Schritt 2: Inline-Edit-Modus für die GBU (nur vor Freigabe verfügbar).
  const [editingGbu, setEditingGbu] = useState(false);
  const [gbuDraft, setGbuDraft] = useState(null);
  const [savingGbu, setSavingGbu] = useState(false);
  // P1.2 Schritt 2: Inline-Edit-Modus für die Betriebsanweisung (nur vor Freigabe).
  const [editingBa, setEditingBa] = useState(false);
  const [baDraft, setBaDraft] = useState(null);
  const [savingBa, setSavingBa] = useState(false);
  // P1.3 Schritt 2: Inline-Edit-Modus für den Unterweisungstext (nur content[].body, vor Freigabe).
  const [editingContent, setEditingContent] = useState(false);
  const [contentDraft, setContentDraft] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  // P3-UX A2: KI-Empfehlungen (ai_recommendations) anzeigen + editieren/löschen (nur vor Freigabe).
  const [editingRecs, setEditingRecs] = useState(false);
  const [recsDraft, setRecsDraft] = useState(null);
  const [savingRecs, setSavingRecs] = useState(false);

  // P0-D3 (2026-07-16) — Readiness-Ampel (reine Anzeige der Backend-Wahrheit
  // aus /api/courses/{id}/release-readiness). Farben ausschließlich aus
  // `readiness.status`. KEINE eigene Vollständigkeitslogik im Frontend.
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState(false);
  const loadReadiness = async (courseId) => {
    if (!courseId) return;
    setReadinessLoading(true);
    setReadinessError(false);
    try {
      const r = await api.getReleaseReadiness(courseId);
      setReadiness(r);
    } catch {
      // Netzwerk-/Serverfehler beim Readiness-Abruf: fail-closed neutral,
      // NICHT als fachliches `blocked` melden und NICHT als `ready` zeigen.
      setReadiness(null);
      setReadinessError(true);
    } finally {
      setReadinessLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await api.getCourse(id);
      setCourse(c);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Dokumentationswelt konnte nicht geladen werden."
      );
    } finally {
      setLoading(false);
    }
  };

  // LIVE-L4 (Betreiberkorrektur §3, 3a): Firmenname aus dem Kaufdatensatz —
  // NUR in der Druckansicht, NUR wenn nicht leer (kein leerer Platzhalter).
  const [druckFirmenname, setDruckFirmenname] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (druckansicht) {
          // STUFE 8H: eingefrorener Kaufstand (purchase.content, Rück-Shim
          // serverseitig) — KEIN Live-Laden des Course-Dokuments, keine
          // Einsatzbereiche, keine Readiness. Nativer fetch (kein axios-Import,
          // hält die bestehenden Jest-Mocks von "@/lib/api" intakt).
          const r = await fetch(
            `${API}/billing/one-time-print-data/${token}?attempt=${encodeURIComponent(attempt || "")}`
          );
          if (!r.ok) throw new Error("print_context_invalid");
          const data = await r.json();
          if (!cancelled) {
            setCourse(data.course);
            setDruckFirmenname(
              typeof data.company_name === "string" ? data.company_name.trim() : ""
            );
          }
          return;
        }
        const [c, ebs] = await Promise.all([
          api.getCourse(id),
          api.listEinsatzbereiche(true),
        ]);
        if (!cancelled) {
          setCourse(c);
          setEinsatzbereiche(ebs);
          // P0-D3: Readiness NUR für noch nicht freigegebene Welten laden
          // (bereits freigegebene sollen keine irreführende Vorab-Ampel sehen).
          if (c && c.is_world && !c.released) {
            loadReadiness(c.id);
          } else {
            setReadiness(null);
            setReadinessError(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
              "Dokumentationswelt konnte nicht geladen werden."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, druckansicht, token, attempt]);

  // IMPLEMENT-14: aktive Welt für das Lehrbild-Overlay setzen (lädt Bild-Manifest).
  useEffect(() => {
    if (course?.id) setActiveLehrbildWorld(course.id);
  }, [course?.id]);

  // IP2-E1C · Desktop-/Owner-Renderkontext (NUR Vorbereitung, KEINE Bildauswahl,
  // KEINE Nutzung von web_derivatives): AUSSCHLIESSLICH die interaktive
  // Owner-Welt setzt ihn — NIE die Druck-/Chromium-PDF-Ansicht (druckansicht),
  // damit Browserdruck und Einmalkauf-PDF garantiert 100 % Master bleiben.
  // Mount/Unmount-Muster exakt analog IP2-D1C (Monteur-Kontext).
  useEffect(() => {
    if (druckansicht) return undefined;
    setLehrbildDesktopKontext(true);
    return () => setLehrbildDesktopKontext(false);
  }, [druckansicht]);

  // IP2-D2 · Print-Renderkontext (Betreiberentscheidung „Print-Consumer"):
  // AUSSCHLIESSLICH die Druck-/Chromium-PDF-Ansicht (druckansicht =
  // Einmalkauf-PDF/Arbeitsschutzmappe) setzt ihn — exakt inverses
  // Mount/Unmount-Muster zum Desktop-Kontext. `useLehrbildImage` wählt dann
  // Print-Derivate (sonst Master-Fallback); die interaktive Owner-Welt
  // bleibt byte-identisch auf Web.
  useEffect(() => {
    if (!druckansicht) return undefined;
    setLehrbildPrintKontext(true);
    return () => setLehrbildPrintKontext(false);
  }, [druckansicht]);

  // P0-BUGFIX (2026-06, Rev. 2 nach Live-Forensik) — Automatischer Readiness-
  // Refresh nach terminalem Abschluss des BESTEHENDEN Bild-Workflows
  // (BLOCK 3E, vsi/lehrbild.jsx — dort ZERO-DIFF). KEIN neuer Timer, KEIN
  // zweites Polling: `useLehrbildImage` abonniert die vorhandene Lehrbild-
  // Runtime (useSyncExternalStore); deren reguläre Notifies lösen ohnehin
  // Re-Renders aus.
  //
  // FORENSIK-BEFUND (FALL A): Die frühere Kante „Poll aktiv → Poll beendet"
  // (`_debugGetPoll`) ist NICHT reaktiv beobachtbar — die Runtime feuert ihr
  // letztes Notify in `_reloadManifest` VOR dem `stop()`, und der Poll-Stopp
  // selbst benachrichtigt nicht. React rendert den letzten Stand, solange
  // der Poll noch registriert ist; die Kante entsteht für React nie.
  // ZUVERLÄSSIGER Vertrag ist der terminale Abschluss-Marker `updated_at`
  // der Runtime (laut deren Doku das „einzige Abschluss-Signal"): er wird
  // VOR dem letzten Notify gesetzt und ist damit an einem garantierten
  // Render beobachtbar. Kante = „nicht terminal geladen" (updated_at ===
  // null nach erfolgtem Fetch) → „terminal" (updated_at gesetzt). Welten,
  // die bereits terminal geladen werden, lösen KEINEN Zusatz-Fetch aus
  // (kein Doppel-GET beim Öffnen längst fertiger Welten).
  useLehrbildImage("", "");
  const lehrbildMeta = course?.id ? _debugGetManifestMeta(course.id) : null;
  const lehrbildTerminal = !!(lehrbildMeta && lehrbildMeta.updated_at);
  const lehrbildLaufendBeobachtet = !!(lehrbildMeta && !lehrbildMeta.updated_at);
  // LIVE-L4: DOM-Marker für den Chromium-Renderweg — "1", sobald der
  // Manifest-Fetch abgeschlossen ist (auch bei leerem/fehlendem Manifest).
  // Erst danach existieren alle Lehrbild-<img>-Elemente der Druckansicht.
  const lehrbildManifestGeladen = !course?.id || !!lehrbildMeta;
  const lehrbildSeenRef = useRef(null);
  const readinessNachholRef = useRef(null);
  // V5I-B (V5G §2): KEIN Auto-Popup mehr — der bestehende Fertigstellungszustand
  // (Manifest terminal ∧ readiness.ready) steuert ausschließlich die SICHTBARKEIT
  // des Buttons „Dokumentation freigeben"; erst dessen Klick öffnet das
  // Zwei-Türen-Popup. Kein neuer Zustand (bestehender useState); nie in der
  // Druckansicht, nie im echten Unternehmer-Modus, nie nach Freigabe.
  const [zweiTuerenOffen, setZweiTuerenOffen] = useState(false);
  const readinessBereit = readiness?.ready === true;
  const zeigeDokumentationFreigeben = sollZweiTuerenPopupOeffnen({
    istOeffentlicheVorschau,
    druckansicht,
    released: !!course?.released,
    lehrbildTerminal,
    readinessReady: readinessBereit,
    bereitsGeoeffnet: false,
  });
  useEffect(() => {
    const worldId = course?.id;
    if (!worldId) return;
    // Welt-Wechsel innerhalb derselben Instanz: fremde Merker verwerfen
    // (kein Refresh einer alten World-ID nach Navigation).
    if (lehrbildSeenRef.current && lehrbildSeenRef.current !== worldId) {
      lehrbildSeenRef.current = null;
    }
    if (readinessNachholRef.current && readinessNachholRef.current !== worldId) {
      readinessNachholRef.current = null;
    }
    if (lehrbildLaufendBeobachtet) {
      lehrbildSeenRef.current = worldId;
      return;
    }
    if (!lehrbildTerminal) return;
    if (lehrbildSeenRef.current !== worldId) return;
    lehrbildSeenRef.current = null;
    if (!course.is_world || course.released) return;
    if (readinessLoading) {
      // Terminales Ereignis darf nicht verloren gehen: genau EIN Nachhol-
      // Refresh nach Abschluss des bereits laufenden Requests, KEIN
      // paralleler Request.
      readinessNachholRef.current = worldId;
      return;
    }
    loadReadiness(worldId);
    // `readinessLoading` absichtlich NICHT in den Dependencies: dieser
    // Effekt reagiert ausschließlich auf die terminale Kante der Runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lehrbildTerminal, lehrbildLaufendBeobachtet, course?.id, course?.is_world, course?.released]);

  // Nachholpfad (F3): reagiert NUR auf das Ende eines laufenden Readiness-
  // Requests (State-Wechsel, kein Timer, kein Polling) und holt einen
  // während des Requests eingetroffenen terminalen Abschluss genau EINMAL
  // nach.
  useEffect(() => {
    if (readinessLoading) return;
    const worldId = course?.id;
    if (!worldId || readinessNachholRef.current !== worldId) return;
    readinessNachholRef.current = null;
    if (course.is_world && !course.released) {
      loadReadiness(worldId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readinessLoading, course?.id, course?.is_world, course?.released]);

  const handleRelease = async () => {
    if (!course || releasing) return;
    setReleasing(true);
    clearPolicyError();
    try {
      const res = await api.releaseCourse(course.id, "Unternehmer");
      toast.success(
        res.document_number
          ? `Welt freigegeben – Welt-Freigabe-Nachweis ${res.document_number} erstellt.`
          : "Welt freigegeben."
      );
      await load();
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen.");
      }
      // P0-D3: Bei 409 (Gate-Verletzung) die Ampel genau einmal neu ziehen,
      // damit der Nutzer die aktuelle Backend-Wahrheit unmittelbar sieht.
      if (e?.response?.status === 409 && course?.id) {
        loadReadiness(course.id);
      }
    } finally {
      setReleasing(false);
    }
  };

  // WELT-ARCHIVIERUNG (Feb 2026, F1–F5) — Confirm-Dialog Öffner.
  // F5: Vor Anzeige des Dialogs die aktuellen Zuweisungs-Zahlen laden,
  // damit die Sicherheitsabfrage konkret ist („X offen, Y überfällig,
  // Z erledigt bleiben in der Historie") statt generisch.
  const openArchiveConfirm = async () => {
    if (!course) return;
    setArchiveAssignmentStats(null);
    setArchiveConfirmOpen(true);
    try {
      const items = await api.listAssignments({ course_id: course.id });
      const arr = Array.isArray(items) ? items : [];
      const offen = arr.filter((a) => a.status === "offen").length;
      const ueberfaellig = arr.filter((a) => a.status === "ueberfaellig").length;
      const erledigt = arr.filter((a) => a.status === "erledigt").length;
      setArchiveAssignmentStats({ offen, ueberfaellig, erledigt, total: arr.length });
    } catch (_e) {
      // Zahlen sind Komfort — Dialog bleibt bedienbar auch ohne.
      setArchiveAssignmentStats({ offen: null, ueberfaellig: null, erledigt: null, total: null });
    }
  };

  const handleArchiveToggle = async () => {
    if (!course || archiveLoading) return;
    const wasArchived = !!course.archived;
    setArchiveLoading(true);
    clearPolicyError();
    try {
      await api.archiveCourse(course.id, !wasArchived);
      toast.success(
        wasArchived
          ? "Welt reaktiviert – wieder in der Bibliothek sichtbar."
          : "Welt archiviert – aus der Bibliothek ausgeblendet."
      );
      setArchiveConfirmOpen(false);
      await load();
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
      }
    } finally {
      setArchiveLoading(false);
    }
  };

  const startHeaderEdit = () => {
    setTitleDraft(course?.title || "");
    setDescDraft(course?.short_description || "");
    setEditingHeader(true);
  };

  const cancelHeaderEdit = () => {
    setEditingHeader(false);
  };

  const saveHeader = async () => {
    if (!course || savingHeader) return;
    const title = titleDraft.trim();
    if (!title) {
      toast.error("Titel darf nicht leer sein.");
      return;
    }
    setSavingHeader(true);
    clearPolicyError();
    try {
      const updated = await api.updateCourse(course.id, {
        title,
        short_description: descDraft.trim(),
      });
      setCourse(updated);
      setEditingHeader(false);
      toast.success("Titel aktualisiert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingHeader(false);
    }
  };

  // --- P1.1 Schritt 2: GBU-Entwurf editieren (nutzt bestehenden PATCH /courses/{id}) ---
  const startGbuEdit = () => {
    const g = course?.gbu || {};
    setGbuDraft({
      hazards: (g.hazards || []).map((h) => ({
        category: h.category || "",
        description: h.description || "",
        measures_technical: h.measures_technical || "",
        measures_organizational: h.measures_organizational || "",
        measures_personal: h.measures_personal || "",
        residual_risk: ["gering", "mittel", "hoch"].includes(h.residual_risk)
          ? h.residual_risk
          : "mittel",
      })),
      responsible_role: g.responsible_role || "",
    });
    setEditingGbu(true);
  };

  const cancelGbuEdit = () => {
    // Verwirft alle Änderungen des aktuellen Edit-Vorgangs; KEIN Backend-Request.
    setEditingGbu(false);
    setGbuDraft(null);
  };

  const updateHazardField = (idx, field, value) => {
    setGbuDraft((d) => ({
      ...d,
      hazards: d.hazards.map((h, i) => (i === idx ? { ...h, [field]: value } : h)),
    }));
  };

  const saveGbu = async () => {
    if (!course || savingGbu || !gbuDraft) return;
    setSavingGbu(true);
    clearPolicyError();
    try {
      // Sendet AUSSCHLIESSLICH die erlaubten GBU-Felder (Whitelist aus Schritt 1).
      const updated = await api.updateCourse(course.id, { gbu: gbuDraft });
      setCourse(updated);
      setEditingGbu(false);
      setGbuDraft(null);
      toast.success("Gefährdungsbeurteilung aktualisiert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingGbu(false);
    }
  };

  // --- P1.2 Schritt 2: Betriebsanweisung editieren (nutzt bestehenden PATCH /courses/{id}) ---
  const startBaEdit = () => {
    const b = course?.betriebsanweisung || {};
    setBaDraft({
      purpose: b.purpose || "",
      main_hazards: [...(b.main_hazards || [])],
      protective_measures: [...(b.protective_measures || [])],
      behavior_in_emergency: b.behavior_in_emergency || "",
      first_aid: b.first_aid || "",
      disposal_or_storage: b.disposal_or_storage || "",
      // Phase C · Baustein 4 Ergänzung (23.08.2026): additives Feld für den
      // eigenen Lagerhinweise-Abschnitt bei hazmat-Welten.
      storage_hints: b.storage_hints || "",
    });
    setEditingBa(true);
  };

  const cancelBaEdit = () => {
    setEditingBa(false);
    setBaDraft(null);
  };

  const updateBaField = (field, value) =>
    setBaDraft((d) => ({ ...d, [field]: value }));

  const updateBaListItem = (field, idx, value) =>
    setBaDraft((d) => ({
      ...d,
      [field]: d[field].map((it, i) => (i === idx ? value : it)),
    }));

  const addBaListItem = (field) =>
    setBaDraft((d) => ({ ...d, [field]: [...d[field], ""] }));

  const removeBaListItem = (field, idx) =>
    setBaDraft((d) => ({ ...d, [field]: d[field].filter((_, i) => i !== idx) }));

  const saveBa = async () => {
    if (!course || savingBa || !baDraft) return;
    setSavingBa(true);
    clearPolicyError();
    try {
      // Datenhygiene: trimmen + vollständig leere Listeneinträge verwerfen.
      // Reihenfolge bleibt exakt erhalten (nur filter/map, kein Sortieren).
      const clean = {
        purpose: baDraft.purpose.trim(),
        main_hazards: baDraft.main_hazards
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        protective_measures: baDraft.protective_measures
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        behavior_in_emergency: baDraft.behavior_in_emergency.trim(),
        first_aid: baDraft.first_aid.trim(),
        disposal_or_storage: baDraft.disposal_or_storage.trim(),
        storage_hints: baDraft.storage_hints.trim(),
      };
      const updated = await api.updateCourse(course.id, { betriebsanweisung: clean });
      setCourse(updated);
      setEditingBa(false);
      setBaDraft(null);
      toast.success("Betriebsanweisung aktualisiert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingBa(false);
    }
  };

  // --- P1.3 Schritt 2: Unterweisungstext editieren (nutzt bestehenden PATCH /courses/{id}) ---
  const startContentEdit = () => {
    const blocks = course?.content || [];
    // heading wird nur zur Anzeige (read-only) mitgeführt; gespeichert wird ausschließlich body.
    setContentDraft(blocks.map((b) => ({ heading: b.heading || "", body: b.body || "" })));
    setEditingContent(true);
  };

  const cancelContentEdit = () => {
    // Verwirft alle Änderungen des aktuellen Edit-Vorgangs; KEIN Backend-Request.
    setEditingContent(false);
    setContentDraft(null);
  };

  const updateContentBody = (idx, value) =>
    setContentDraft((d) => d.map((b, i) => (i === idx ? { ...b, body: value } : b)));

  const saveContent = async () => {
    if (!course || savingContent || !contentDraft) return;
    setSavingContent(true);
    clearPolicyError();
    try {
      // Sendet AUSSCHLIESSLICH content[].body (Whitelist aus Schritt 1). `heading`
      // bleibt serverseitig unverändert; die Reihenfolge der Blöcke bleibt exakt erhalten.
      const payload = { content: contentDraft.map((b) => ({ body: b.body })) };
      const updated = await api.updateCourse(course.id, payload);
      setCourse(updated);
      setEditingContent(false);
      setContentDraft(null);
      toast.success("Unterweisung aktualisiert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingContent(false);
    }
  };

  // --- P3-UX A2: KI-Empfehlungen anzeigen/editieren/löschen (nutzt bestehenden PATCH /courses/{id}) ---
  const recLabel = (t) =>
    t === "practical_instruction" ? "Praktische Einweisung empfohlen" : "KI-Empfehlung";

  const startRecsEdit = () => {
    const recs = course?.ai_recommendations || [];
    setRecsDraft(recs.map((r) => ({ type: r.type, text: r.text || "", suggested: r.suggested !== false })));
    setEditingRecs(true);
  };

  const cancelRecsEdit = () => {
    setEditingRecs(false);
    setRecsDraft(null);
  };

  const updateRecText = (idx, value) =>
    setRecsDraft((d) => d.map((r, i) => (i === idx ? { ...r, text: value } : r)));

  const saveRecs = async () => {
    if (!course || savingRecs || !recsDraft) return;
    setSavingRecs(true);
    clearPolicyError();
    try {
      // Sendet AUSSCHLIESSLICH ai_recommendations (Whitelist im Backend). Nicht
      // beweistragend → kein Einfluss auf Snapshot/WORM/legal_basis.
      const updated = await api.updateCourse(course.id, { ai_recommendations: recsDraft });
      setCourse(updated);
      setEditingRecs(false);
      setRecsDraft(null);
      toast.success("KI-Empfehlung aktualisiert.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSavingRecs(false);
    }
  };

  const deleteRec = async (idx) => {
    if (!course || savingRecs) return;
    // Löschen = Eintrag vollständig entfernen (keine Nachfrage). Leere Liste erlaubt.
    const remaining = (course.ai_recommendations || []).filter((_, i) => i !== idx);
    setSavingRecs(true);
    clearPolicyError();
    try {
      const updated = await api.updateCourse(course.id, { ai_recommendations: remaining });
      setCourse(updated);
      toast.success("KI-Empfehlung entfernt.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
      }
    } finally {
      setSavingRecs(false);
    }
  };

  const handleDiscard = async () => {
    if (!course || discarding) return;
    setDiscarding(true);
    try {
      await api.deleteCourse(course.id);
      toast.success("Welt verworfen.");
      navigate("/unternehmer/unterweisungen");
    } catch (e) {
      if (!handlePolicyWithToast(e)) {
        toast.error(e?.response?.data?.detail || "Verwerfen fehlgeschlagen.");
      }
      setDiscarding(false);
      setDiscardOpen(false);
    }
  };

  // Sprint C – Versions-Bump für bereits freigegebene Welten.
  const openNewVersion = () => {
    setNvTitleDraft(course?.title || "");
    setNvDescDraft(course?.short_description || "");
    setNewVersionResult(null);
    setNewVersionOpen(true);
  };

  const submitNewVersion = async () => {
    if (!course || nvSubmitting) return;
    const title = nvTitleDraft.trim();
    if (!title) {
      toast.error("Titel darf nicht leer sein.");
      return;
    }
    setNvSubmitting(true);
    clearPolicyError();
    try {
      const res = await api.releaseNewVersion(course.id, {
        title,
        short_description: nvDescDraft.trim(),
      });
      setCourse(res.course);
      setNewVersionResult(res);
      toast.success(
        `Neue Version v${res.new_version} freigegeben.${
          res.affected_employees
            ? ` ${res.affected_employees} Mitarbeiter erhalten die neue Aufgabe.`
            : ""
        }`
      );
    } catch (e) {
      if (!handlePolicyWithToast(e)) {
        toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen.");
      }
    } finally {
      setNvSubmitting(false);
    }
  };

  const closeNewVersion = () => {
    setNewVersionOpen(false);
    setNewVersionResult(null);
  };

  if (loading) {
    return (
      <div
        className="max-w-4xl mx-auto py-20 text-center"
        data-testid="welt-loading"
      >
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
        <p className="text-sm text-slate-500 mt-3">
          Dokumentationswelt wird geladen…
        </p>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-4xl mx-auto py-12" data-testid="welt-error">
        <Card className="bg-white border-slate-200 p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            {error || "Welt nicht gefunden."}
          </h2>
          <Button
            variant="outline"
            onClick={() => navigate("/unternehmer/unterweisungen")}
            className="mt-6"
            data-testid="welt-back-on-error"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu den Gefährdungsbeurteilungen
          </Button>
        </Card>
      </div>
    );
  }

  // Falls jemand versucht, hier ein reines „Thema" (kein Subjekt) zu öffnen,
  // sanft zur Bibliothek umlenken – diese Seite ist für Welten.
  if (!course.is_world) {
    return (
      <div className="max-w-4xl mx-auto py-12" data-testid="welt-not-a-world">
        <Card className="bg-white border-slate-200 p-8 text-center">
          <BookOpen className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            {"\u201E"}{course.title}{"\u201C"} ist ein Thema, keine Dokumentationswelt.
          </h2>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Themen-Unterweisungen sind sofort einsatzbereit und können direkt
            aus den Gefährdungsbeurteilungen zugewiesen werden.
          </p>
          <Button
            onClick={() => navigate("/unternehmer/unterweisungen")}
            className="mt-6 bg-slate-900 hover:bg-slate-800"
            data-testid="welt-go-library"
          >
            Zu den Gefährdungsbeurteilungen
          </Button>
        </Card>
      </div>
    );
  }

  const gbu = course.gbu || {};
  const ba = course.betriebsanweisung || {};
  const wartung = course.wartung || null;
  const hazards = gbu.hazards || [];
  const protective = ba.protective_measures || [];
  const mainHazards = ba.main_hazards || [];
  const inspectionItems = wartung?.inspection_items || [];

  // DEMO-SAFE-2: vorhandene Visual Learning Objects priorisiert & fail-closed.
  // Leer/undefined/fehlerhaft -> nichts anzeigen, kein Crash.
  const visualObjects = Array.isArray(course.visual_objects)
    ? course.visual_objects
        .filter((o) => o && VLO_DISPLAY_ORDER[o.vlo_type] != null)
        .sort(
          (a, b) => VLO_DISPLAY_ORDER[a.vlo_type] - VLO_DISPLAY_ORDER[b.vlo_type]
        )
    : [];

  // IMPLEMENT-7 / VLO-RICHTIG-FALSCH: erster visueller Lernbaustein, rein
  // frontendseitig aus vorhandenen Betriebsanweisungs-Texten gebaut. Fail-closed:
  // ohne verwertbare Texte -> null (Block entfällt).
  const richtigFalsch = buildRichtigFalsch(course);

  return (
    <div
      className={`max-w-4xl mx-auto space-y-6${druckansicht ? " welt-druckansicht" : ""}`}
      data-testid="welt-page"
      data-lehrbild-manifest-geladen={lehrbildManifestGeladen ? "1" : "0"}
    >
      {druckansicht && (
        <style>{`
          /* STUFE 8H — AUSSCHLIESSLICH druckbedingte Regeln (A4-Umbrüche,
             Ausblenden interaktiver Elemente, Farberhalt). KEINE Gestaltung. */
          html, body {
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .welt-druckansicht [data-testid="welt-title-edit-button"],
          .welt-druckansicht [data-testid="welt-gbu-edit-button"],
          .welt-druckansicht [data-testid="welt-ba-edit-button"],
          .welt-druckansicht [data-testid="welt-content-edit-button"],
          .welt-druckansicht [data-testid^="welt-recs-edit-button"],
          .welt-druckansicht [data-testid^="welt-recs-delete-button"] {
            display: none !important;
          }
          .welt-druckansicht [data-testid="welt-disclaimer-banner"],
          .welt-druckansicht [data-testid^="welt-hazard-"],
          .welt-druckansicht [data-testid^="welt-content-block-"],
          .welt-druckansicht [data-testid^="welt-inspection-"],
          .welt-druckansicht [data-testid^="welt-recs-item-"],
          .welt-druckansicht [data-testid^="hazard-marker-"],
          .welt-druckansicht [data-testid^="richtig-falsch-richtig-card-"],
          .welt-druckansicht [data-testid^="richtig-falsch-falsch-card-"],
          .welt-druckansicht [data-testid="learning-focus"],
          .welt-druckansicht [data-testid="psa-summary"],
          .welt-druckansicht [data-testid="safety-symbols-bar"] {
            break-inside: avoid;
          }
          /* Einzelkauf-Anhangseiten (Juni 2026): Unterweisungsnachweis (fest
             6 Zeilen, Muster V4H) + Wirksamkeitskontroll-Checkliste — je eine
             eigene A4-Seite am Dokumentende. */
          .welt-druck-anhangseite {
            break-before: page;
            page-break-before: always;
          }
          .welt-druck-anhangseite tr,
          .welt-druck-anhangseite [data-testid^="welt-druck-wk-punkt-"] {
            break-inside: avoid;
          }
        `}</style>
      )}
      {policyError && (
        <AccountPolicyBanner error={policyError} onDismiss={clearPolicyError} />
      )}
      {/* Back-Link (STUFE 6: nicht im anonymen Vorschau-Flow) */}
      {!istOeffentlicheVorschau && (
        <button
          onClick={() => navigate("/unternehmer/unterweisungen")}
          data-testid="welt-back-button"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zu den Gefährdungsbeurteilungen
        </button>
      )}

      {/* DEMO-SAFE-3: Plakat öffnen (neue Tab, reine Druck-/Lernansicht).
          STUFE 6: nicht im anonymen Vorschau-Flow. Stufe 4.3A: nur mit Vollzugang. */}
      {plakatErlaubt && (
        <div className="flex justify-end -mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              window.open(`/unternehmer/welt/${course.id}/plakat`, "_blank")
            }
            data-testid="welt-open-plakat-button"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Printer className="w-4 h-4 mr-2" />
            Plakat öffnen
          </Button>
        </div>
      )}

      {/* Header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Dokumentationswelt
          </span>
          {course.subject_type && (
            <Badge
              variant="outline"
              className="bg-slate-100 border-slate-200 text-slate-700 text-[10px] uppercase tracking-wider"
              data-testid="welt-subject-type"
            >
              {SUBJECT_TYPE_LABEL[course.subject_type] || course.subject_type}
            </Badge>
          )}
          {course.source === "ai_generated" && (
            <Badge
              variant="outline"
              className="bg-slate-100 border-slate-200 text-slate-700 text-[10px] tracking-wider inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              CLartext.io-erstellt
            </Badge>
          )}
        </div>
        {editingHeader ? (
          <div className="mt-2 space-y-3" data-testid="welt-header-edit">
            <Input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              data-testid="welt-title-input"
              placeholder="Titel"
              className="h-14 text-2xl sm:text-3xl font-heading font-bold border-slate-300"
              ref={safeAutoFocusRef}
            />
            <Textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              data-testid="welt-description-input"
              placeholder="Kurzbeschreibung (optional)"
              rows={2}
              className="text-base border-slate-300"
            />
            <div className="flex gap-2">
              <Button
                onClick={saveHeader}
                disabled={savingHeader}
                data-testid="welt-title-save"
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {savingHeader ? "Speichert…" : "Speichern"}
              </Button>
              <Button
                variant="outline"
                onClick={cancelHeaderEdit}
                disabled={savingHeader}
                data-testid="welt-title-cancel"
              >
                <X className="w-4 h-4 mr-1.5" />
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-2 flex items-start gap-2 group">
              <h1
                className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 flex-1 min-w-0"
                data-testid="welt-title"
              >
                {course.title}
              </h1>
              {!course.released && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startHeaderEdit}
                  data-testid="welt-title-edit-button"
                  className="h-8 w-8 p-0 text-slate-400 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1"
                  aria-label="Titel bearbeiten"
                  title="Titel & Beschreibung bearbeiten"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </div>
            <p className="text-slate-600 mt-2 max-w-2xl">
              {course.short_description}
            </p>
            {druckansicht && druckFirmenname ? (
              <div
                className="text-sm text-slate-700 mt-1"
                data-testid="welt-druck-firmenname"
              >
                {druckFirmenname}
              </div>
            ) : null}
          </>
        )}

        {/* Einsatzbereich-Verteilung (Phase 3) — STUFE 6: kompletter
            Verwaltungsblock nicht im anonymen Vorschau-Flow. */}
        {!istOeffentlicheVorschau && (
          <WeltEinsatzbereichRow
            course={course}
            allEinsatzbereiche={einsatzbereiche}
            onEdit={() => setEbDialogOpen(true)}
          />
        )}
      </div>

      {/* Phase C · Etappe 2 · F5b SDB-Status-Banner (Gefahrstoffmodul, 22.08.2026) */}
      <HazmatSdbStatusBanner
        status={course.hazmat_sdb_status}
        documentNumber={course.hazmat_sdb_document_number}
        sourceUrl={course.hazmat_sdb_source_url}
      />

      {/* Freigabe-Banner (oben sichtbar, wenn noch nicht freigegeben) */}
      {!course.released ? (
        <Card
          className="bg-amber-50 border border-amber-200 px-5 py-4"
          data-testid="welt-disclaimer-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-amber-900">
                Standardvorlage – betrieblich anzupassen
              </div>
              <p className="text-sm text-amber-800 mt-1">
                Diese Welt wurde von CLartext.io mithilfe von Künstlicher
                Intelligenz erzeugt. Prüfen Sie alle Bausteine, passen Sie
                sie ggf. an die Realität Ihres Betriebs an und geben Sie sie
                frei. Erst nach der Freigabe kann sie an Mitarbeiter
                zugewiesen werden.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card
          className="bg-emerald-50 border border-emerald-200 px-5 py-4"
          data-testid="welt-released-banner"
        >
          {/* MOBIL-WELT-02, Betreiberfreigabe 28.10.2026 — nur Bildschirm bis 767 px:
              Zeile umbrechen, Knöpfe darunter voll breit, 48 px; PDF und ab 768 px unverändert */}
          <style>{`
            @media screen and (max-width: 767px) {
              .welt-freigabe-zeile { flex-wrap: wrap; }
              .welt-freigabe-knoepfe { width: 100%; flex-direction: column; align-items: stretch; }
              .welt-freigabe-knoepfe > button { width: 100%; min-height: 48px; font-size: 14px; }
            }
          `}</style>
          {/* MOBIL-WELT-02, Betreiberfreigabe 28.10.2026 — Klasse welt-freigabe-zeile */}
          <div className="welt-freigabe-zeile flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-emerald-900">
                Freigegeben am {formatDate(course.released_at)} durch{" "}
                {course.released_by || "Unternehmer"}{" "}
                <span className="ml-1 inline-block text-xs px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-800 align-middle"
                      data-testid="welt-version-badge">
                  v{course.version || "1.0"}
                </span>
                {course.archived && (
                  <span
                    className="ml-2 inline-block text-xs px-1.5 py-0.5 rounded bg-slate-200 border border-slate-300 text-slate-700 align-middle uppercase tracking-wider"
                    data-testid="welt-archived-badge"
                  >
                    Archiviert
                  </span>
                )}
              </div>
              <p className="text-sm text-emerald-800 mt-1">
                {course.archived ? (
                  <>
                    Diese Welt ist <strong>archiviert</strong> und wird
                    ausgeblendet: keine neuen Zuweisungen, nicht in Bibliothek/
                    Nachschlagewerk sichtbar. Bestehende Nachweise bleiben in
                    der Prüfmappe unverändert erhalten.
                  </>
                ) : (
                  <>
                    Diese Welt ist als Standardvorlage des Betriebs freigegeben und
                    kann zugewiesen werden. Fachliche Änderungen werden als
                    neue Version freigegeben — bestehende Aufgaben werden dann
                    automatisch ersetzt.
                  </>
                )}
              </p>
            </div>
            {/* MOBIL-WELT-02, Betreiberfreigabe 28.10.2026 — Klasse welt-freigabe-knoepfe */}
            <div className="welt-freigabe-knoepfe shrink-0 flex items-center gap-2">
              {!course.archived && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openNewVersion}
                  data-testid="welt-release-new-version"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />
                  Als neue Version freigeben
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={
                  course.archived
                    ? handleArchiveToggle
                    : openArchiveConfirm
                }
                data-testid="welt-archive-toggle"
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
                disabled={archiveLoading}
              >
                {course.archived ? (
                  <>
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />
                    Reaktivieren
                  </>
                ) : (
                  <>
                    <Archive className="w-3.5 h-3.5 mr-1.5" />
                    Archivieren
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 1. Gefährdungsbeurteilung */}
      <SectionCard
        icon={ShieldAlert}
        title="Gefährdungsbeurteilung"
        testId="welt-section-gbu"
      >
        {editingGbu ? (
          <div className="space-y-4" data-testid="welt-gbu-edit-form">
            {gbuDraft.hazards.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                Keine Gefährdungen vorhanden – nur die verantwortliche Rolle kann
                bearbeitet werden.
              </p>
            ) : (
              gbuDraft.hazards.map((h, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-slate-200 p-4 space-y-3"
                  data-testid={`welt-gbu-edit-hazard-${idx}`}
                >
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                        Kategorie
                      </label>
                      <Input
                        value={h.category}
                        onChange={(e) => updateHazardField(idx, "category", e.target.value)}
                        data-testid={`welt-gbu-hazard-${idx}-category`}
                        className="mt-1 border-slate-300"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                        Restrisiko
                      </label>
                      <select
                        value={h.residual_risk}
                        onChange={(e) => updateHazardField(idx, "residual_risk", e.target.value)}
                        data-testid={`welt-gbu-hazard-${idx}-risk`}
                        className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                      >
                        <option value="gering">gering</option>
                        <option value="mittel">mittel</option>
                        <option value="hoch">hoch</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Beschreibung
                    </label>
                    <Textarea
                      value={h.description}
                      onChange={(e) => updateHazardField(idx, "description", e.target.value)}
                      data-testid={`welt-gbu-hazard-${idx}-description`}
                      rows={2}
                      className="mt-1 border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Technisch
                    </label>
                    <Textarea
                      value={h.measures_technical}
                      onChange={(e) => updateHazardField(idx, "measures_technical", e.target.value)}
                      data-testid={`welt-gbu-hazard-${idx}-technical`}
                      rows={2}
                      className="mt-1 border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Organisatorisch
                    </label>
                    <Textarea
                      value={h.measures_organizational}
                      onChange={(e) => updateHazardField(idx, "measures_organizational", e.target.value)}
                      data-testid={`welt-gbu-hazard-${idx}-organizational`}
                      rows={2}
                      className="mt-1 border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Persönlich
                    </label>
                    <Textarea
                      value={h.measures_personal}
                      onChange={(e) => updateHazardField(idx, "measures_personal", e.target.value)}
                      data-testid={`welt-gbu-hazard-${idx}-personal`}
                      rows={2}
                      className="mt-1 border-slate-300"
                    />
                  </div>
                </div>
              ))
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Verantwortliche Rolle
              </label>
              <Input
                value={gbuDraft.responsible_role}
                onChange={(e) =>
                  setGbuDraft((d) => ({ ...d, responsible_role: e.target.value }))
                }
                data-testid="welt-gbu-responsible-input"
                className="mt-1 border-slate-300"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={saveGbu}
                disabled={savingGbu}
                data-testid="welt-gbu-save"
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {savingGbu ? "Speichert…" : "Speichern"}
              </Button>
              <Button
                variant="outline"
                onClick={cancelGbuEdit}
                disabled={savingGbu}
                data-testid="welt-gbu-cancel"
              >
                <X className="w-4 h-4 mr-1.5" />
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <>
            {!course.released && (
              <div className="flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startGbuEdit}
                  data-testid="welt-gbu-edit-button"
                  className="text-slate-600"
                >
                  <Pencil className="w-4 h-4 mr-1.5" />
                  Gefährdungsbeurteilung bearbeiten
                </Button>
              </div>
            )}
            {hazards.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                Keine Gefährdungen erfasst.
              </p>
            ) : (
              <div className="space-y-3" data-testid="welt-gbu-hazards">
                {hazards.map((h, idx) => (
                  <div
                    key={idx}
                    className="rounded-md border border-slate-200 p-4"
                    data-testid={`welt-hazard-${idx}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge
                        variant="outline"
                        className="bg-slate-100 border-slate-200 text-slate-700 text-[10px] uppercase tracking-wider"
                      >
                        {h.category}
                      </Badge>
                      {h.residual_risk && (
                        <Badge
                          variant="outline"
                          className={`${RESIDUAL_RISK_COLOR[h.residual_risk] || "bg-slate-50"} text-[10px] uppercase tracking-wider`}
                        >
                          Restrisiko: {h.residual_risk}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-900 font-medium">
                      {h.description}
                    </p>
                    <dl className="mt-3 space-y-2 text-sm">
                      {h.measures_technical && (
                        <div className="flex gap-2">
                          <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium w-32 shrink-0 pt-0.5">
                            Technisch
                          </dt>
                          <dd className="text-slate-700">{h.measures_technical}</dd>
                        </div>
                      )}
                      {h.measures_organizational && (
                        <div className="flex gap-2">
                          <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium w-32 shrink-0 pt-0.5">
                            Organisatorisch
                          </dt>
                          <dd className="text-slate-700">
                            {h.measures_organizational}
                          </dd>
                        </div>
                      )}
                      {h.measures_personal && (
                        <div className="flex gap-2">
                          <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium w-32 shrink-0 pt-0.5">
                            Persönlich
                          </dt>
                          <dd className="text-slate-700">{h.measures_personal}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                ))}
              </div>
            )}
            {gbu.responsible_role && (
              <p className="text-xs text-slate-500 mt-4">
                Verantwortlich: <span className="font-medium">{gbu.responsible_role}</span>
              </p>
            )}
          </>
        )}
        {/* Schritt 4c: Rechtsgrundlagen (list[str]) – reine Anzeige aus dem
            evidence-neutralen Top-Level-Feld course.legal_basis. Reihenfolge
            EXAKT wie Backend (kein Sortieren/Filtern/Umordnen). Fehlt/[] →
            nichts anzeigen (kein rohes []/undefined/null, kein Crash).
            Read-only: legal_basis_is_suggested wird nur gelesen, nie geschrieben. */}
        {Array.isArray(course.legal_basis) && course.legal_basis.length > 0 && (
          <div className="mt-4" data-testid="welt-gbu-legal-basis">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1">
              Rechtsgrundlagen
            </p>
            <ul className="text-sm text-slate-700 list-disc pl-5 space-y-0.5">
              {course.legal_basis.map((lb, i) => (
                <li key={i} data-testid={`welt-gbu-legal-basis-item-${i}`}>
                  {lb}
                </li>
              ))}
            </ul>
            {course.legal_basis_is_suggested === true && (
              <p
                className="text-xs text-amber-700 mt-2"
                data-testid="welt-gbu-legal-basis-hint"
              >
                Die vorgeschlagenen Rechtsgrundlagen wurden anhand der
                beschriebenen Tätigkeit automatisch ermittelt. Bitte fachlich
                prüfen.
              </p>
            )}
          </div>
        )}
      </SectionCard>

      {/* 2. Betriebsanweisung */}
      <SectionCard
        icon={FileWarning}
        title="Betriebsanweisung"
        subtitle="Nach TRBS / GefStoffV / BetrSichV"
        testId="welt-section-betriebsanweisung"
      >
        {editingBa ? (
          <div className="space-y-4" data-testid="welt-ba-edit-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Zweck
              </label>
              <Textarea
                value={baDraft.purpose}
                onChange={(e) => updateBaField("purpose", e.target.value)}
                data-testid="welt-ba-purpose"
                rows={2}
                className="mt-1 border-slate-300"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Hauptgefahren
              </label>
              <div className="space-y-2 mt-1" data-testid="welt-ba-main-hazards-editor">
                {baDraft.main_hazards.map((h, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={h}
                      onChange={(e) => updateBaListItem("main_hazards", i, e.target.value)}
                      data-testid={`welt-ba-main-hazard-${i}`}
                      className="border-slate-300"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeBaListItem("main_hazards", i)}
                      data-testid={`welt-ba-main-hazard-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addBaListItem("main_hazards")}
                  data-testid="welt-ba-main-hazard-add"
                  className="text-slate-600"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Hauptgefahr hinzufügen
                </Button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Schutzmaßnahmen & Verhaltensregeln
              </label>
              <div className="space-y-2 mt-1" data-testid="welt-ba-protective-editor">
                {baDraft.protective_measures.map((m, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={m}
                      onChange={(e) => updateBaListItem("protective_measures", i, e.target.value)}
                      data-testid={`welt-ba-protective-${i}`}
                      className="border-slate-300"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeBaListItem("protective_measures", i)}
                      data-testid={`welt-ba-protective-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addBaListItem("protective_measures")}
                  data-testid="welt-ba-protective-add"
                  className="text-slate-600"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Schutzmaßnahme hinzufügen
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Im Notfall
                </label>
                <Textarea
                  value={baDraft.behavior_in_emergency}
                  onChange={(e) => updateBaField("behavior_in_emergency", e.target.value)}
                  data-testid="welt-ba-behavior"
                  rows={2}
                  className="mt-1 border-slate-300"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Erste Hilfe
                </label>
                <Textarea
                  value={baDraft.first_aid}
                  onChange={(e) => updateBaField("first_aid", e.target.value)}
                  data-testid="welt-ba-first-aid"
                  rows={2}
                  className="mt-1 border-slate-300"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  {course.subject_type === "hazmat"
                    ? "Entsorgung"
                    : "Lagerung / Entsorgung / Außerbetriebnahme"}
                </label>
                <Textarea
                  value={baDraft.disposal_or_storage}
                  onChange={(e) => updateBaField("disposal_or_storage", e.target.value)}
                  data-testid="welt-ba-disposal"
                  rows={2}
                  className="mt-1 border-slate-300"
                />
              </div>
              {course.subject_type === "hazmat" && (
                <div className="sm:col-span-2" data-testid="welt-ba-storage-hints-editor">
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Lagerhinweise (eigener Abschnitt unterhalb der Betriebsanweisung)
                  </label>
                  <Textarea
                    value={baDraft.storage_hints}
                    onChange={(e) => updateBaField("storage_hints", e.target.value)}
                    data-testid="welt-ba-storage-hints"
                    rows={3}
                    className="mt-1 border-slate-300"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={saveBa}
                disabled={savingBa}
                data-testid="welt-ba-save"
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {savingBa ? "Speichert…" : "Speichern"}
              </Button>
              <Button
                variant="outline"
                onClick={cancelBaEdit}
                disabled={savingBa}
                data-testid="welt-ba-cancel"
              >
                <X className="w-4 h-4 mr-1.5" />
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <>
            {!course.released && (
              <div className="flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startBaEdit}
                  data-testid="welt-ba-edit-button"
                  className="text-slate-600"
                >
                  <Pencil className="w-4 h-4 mr-1.5" />
                  Betriebsanweisung bearbeiten
                </Button>
              </div>
            )}
            {ba.purpose && (
              <p className="text-sm text-slate-700 mb-4">{ba.purpose}</p>
            )}

            {mainHazards.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                  Hauptgefahren
                </h3>
                <ul className="space-y-1.5">
                  {mainHazards.map((h, i) => (
                    <li
                      key={i}
                      className="text-sm text-slate-700 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-slate-400"
                    >
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {protective.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                  Schutzmaßnahmen & Verhaltensregeln
                </h3>
                <ul className="space-y-1.5">
                  {protective.map((m, i) => (
                    <li
                      key={i}
                      className="text-sm text-slate-700 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-slate-400"
                    >
                      {course.subject_type === "hazmat" ? (
                        <NichtAusSdbText text={m} />
                      ) : (
                        m
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {ba.behavior_in_emergency && (
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                    Im Notfall
                  </h3>
                  <p className="text-sm text-slate-700">
                    {course.subject_type === "hazmat" ? (
                      <NichtAusSdbText text={ba.behavior_in_emergency} />
                    ) : (
                      ba.behavior_in_emergency
                    )}
                  </p>
                </div>
              )}
              {ba.first_aid && (
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                    Erste Hilfe
                  </h3>
                  <p className="text-sm text-slate-700">
                    {course.subject_type === "hazmat" ? (
                      <NichtAusSdbText text={ba.first_aid} />
                    ) : (
                      ba.first_aid
                    )}
                  </p>
                </div>
              )}
              {ba.disposal_or_storage && (
                <div className="sm:col-span-2">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                    {course.subject_type === "hazmat"
                      ? // MP-3.7 (23.08.2026): Bei hazmat + separatem storage_hints
                        // steht hier nur noch die Entsorgung. Bei Alt-Welten ohne
                        // storage_hints bleibt das Label „Lagerung & Entsorgung".
                        ba.storage_hints
                        ? "Entsorgung"
                        : "Lagerung & Entsorgung"
                      : course.subject_type === "vehicle"
                        ? "Abstellen"
                        : "Außerbetriebnahme"}
                  </h3>
                  <p className="text-sm text-slate-700">
                    {course.subject_type === "hazmat" ? (
                      <NichtAusSdbText text={ba.disposal_or_storage} />
                    ) : (
                      ba.disposal_or_storage
                    )}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </SectionCard>

      {/* Phase C · Baustein 4 (23.08.2026): eigener Lagerhinweise-Abschnitt
          unterhalb der Betriebsanweisung. Erscheint AUSSCHLIESSLICH bei
          subject_type=hazmat, wenn `betriebsanweisung.storage_hints` gesetzt
          ist (Alt-Welten ohne dieses Feld bleiben unverändert — die Lagerung
          wird dann weiter innerhalb der Betriebsanweisung angezeigt). */}
      {course.subject_type === "hazmat" &&
        course.betriebsanweisung?.storage_hints && (
          <SectionCard
            icon={Package}
            title="Lagerhinweise"
            subtitle="Aus SDB Abschnitt 7 – ergänzt, wo im SDB nicht abgedeckt"
            testId="welt-section-lagerhinweise"
          >
            <p
              className="text-sm text-slate-700"
              data-testid="welt-storage-hints-text"
            >
              <NichtAusSdbText text={course.betriebsanweisung.storage_hints} />
            </p>
          </SectionCard>
        )}

      {/* 3. Unterweisung */}
      <SectionCard
        icon={BookOpen}
        title="Unterweisung"
        subtitle="Nach DGUV V1 §4 · ca. 2–3 Min. Lesezeit"
        testId="welt-section-unterweisung"
      >
        {editingContent ? (
          <div className="space-y-4" data-testid="welt-content-edit-form">
            {contentDraft.map((block, idx) => (
              <div key={idx} data-testid={`welt-content-edit-block-${idx}`}>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  {block.heading}
                </label>
                <Textarea
                  value={block.body}
                  onChange={(e) => updateContentBody(idx, e.target.value)}
                  data-testid={`welt-content-body-${idx}`}
                  rows={4}
                  className="mt-1 border-slate-300"
                />
              </div>
            ))}
            <div className="flex gap-2">
              <Button
                onClick={saveContent}
                disabled={savingContent}
                data-testid="welt-content-save"
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {savingContent ? "Speichert…" : "Speichern"}
              </Button>
              <Button
                variant="outline"
                onClick={cancelContentEdit}
                disabled={savingContent}
                data-testid="welt-content-cancel"
              >
                <X className="w-4 h-4 mr-1.5" />
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <>
            {!course.released && (
              <div className="flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startContentEdit}
                  data-testid="welt-content-edit-button"
                  className="text-slate-600"
                >
                  <Pencil className="w-4 h-4 mr-1.5" />
                  Unterweisung bearbeiten
                </Button>
              </div>
            )}
            <div className="space-y-4">
              {(course.content || []).map((block, idx) => (
                <div key={idx} data-testid={`welt-content-block-${idx}`}>
                  <h3 className="font-heading font-semibold text-slate-900 text-sm mb-1">
                    {block.heading}
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {block.body}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </SectionCard>

      {/* 4. KI-Empfehlungen (P3-UX A2) – rein beratend, editierbar/löschbar, NICHT
          beweistragend. Ergänzt das Dokument, ersetzt keinen fachlichen Inhalt.
          Fail-closed: nur anzeigen, wenn Empfehlungen vorhanden sind. */}
      {Array.isArray(course.ai_recommendations) && course.ai_recommendations.length > 0 && (
        <SectionCard
          icon={Sparkles}
          title="KI-Empfehlungen"
          subtitle="Beratende Hinweise der KI – Sie entscheiden, ob Sie sie übernehmen"
          testId="welt-section-ai-recommendations"
        >
          {editingRecs ? (
            <div className="space-y-4" data-testid="welt-recs-edit-form">
              {recsDraft.map((rec, idx) => (
                <div key={idx} data-testid={`welt-recs-edit-item-${idx}`}>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    {recLabel(rec.type)}
                  </label>
                  <Textarea
                    value={rec.text}
                    onChange={(e) => updateRecText(idx, e.target.value)}
                    data-testid={`welt-recs-text-${idx}`}
                    rows={4}
                    className="mt-1 border-slate-300"
                  />
                </div>
              ))}
              <div className="flex gap-2">
                <Button
                  onClick={saveRecs}
                  disabled={savingRecs}
                  data-testid="welt-recs-save"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Check className="w-4 h-4 mr-1.5" />
                  {savingRecs ? "Speichert…" : "Speichern"}
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelRecsEdit}
                  disabled={savingRecs}
                  data-testid="welt-recs-cancel"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Abbrechen
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {(course.ai_recommendations || []).map((rec, idx) => (
                <div
                  key={idx}
                  data-testid={`welt-recs-item-${idx}`}
                  className="rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3"
                >
                  <div className="flex items-start gap-2.5">
                    <Lightbulb className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{recLabel(rec.type)}</p>
                      <p className="text-sm text-slate-700 leading-relaxed mt-1" data-testid={`welt-recs-text-view-${idx}`}>
                        {rec.text}
                      </p>
                      <p className="text-xs text-slate-500 leading-relaxed mt-2">
                        Diese Empfehlung dient ausschließlich als Unterstützung und ersetzt
                        keine eigene fachliche Prüfung.
                      </p>
                    </div>
                  </div>
                  {!course.released && (
                    <div className="flex gap-2 mt-3 pl-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={startRecsEdit}
                        data-testid={`welt-recs-edit-button-${idx}`}
                        className="text-slate-600"
                      >
                        <Pencil className="w-4 h-4 mr-1.5" />
                        Bearbeiten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteRec(idx)}
                        disabled={savingRecs}
                        data-testid={`welt-recs-delete-button-${idx}`}
                        className="text-slate-600"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Löschen
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* 3b. Visuelle Lernhilfen (DEMO-SAFE-2) – rein informativ, additiv.
          Verändert keine Inhalte, Nachweise oder PDFs. Fail-closed: nichts
          anzeigen, wenn keine Visual Learning Objects vorhanden sind. */}
      {(visualObjects.length > 0 || richtigFalsch) && (
        <SectionCard
          icon={Eye}
          title="Visuelle Lernhilfen"
          subtitle="Unterstützen das Verständnis – verändern keine Inhalte, Nachweise oder PDFs"
          testId="welt-section-visual-objects"
        >
          <div className="space-y-4" data-testid="welt-visual-objects">
            <VisualObjects objects={visualObjects} lang="de" />
            {richtigFalsch && <RichtigFalschRenderer vlo={richtigFalsch} lang="de" />}
          </div>
        </SectionCard>
      )}

      {/* 4. Verständnisfragen — STUFE 8H: entfallen in der Druckansicht
          (eingefrorene Betreiberentscheidung: Quiz wird NICHT gedruckt). */}
      {!druckansicht && (
      <SectionCard
        icon={HelpCircle}
        title="Verständnisfragen"
        subtitle={`${(course.questions || []).length} Fragen, je 3 Antwortoptionen`}
        testId="welt-section-questions"
      >
        <div className="space-y-4">
          {(course.questions || []).map((q, idx) => (
            <div
              key={q.id || idx}
              className="rounded-md border border-slate-200 p-4"
              data-testid={`welt-question-${idx}`}
            >
              <p className="text-sm font-medium text-slate-900 mb-2">
                {idx + 1}. {q.text}
              </p>
              <ul className="space-y-1">
                {q.options.map((opt, i) => (
                  <li
                    key={i}
                    className={`text-sm pl-5 relative ${
                      i === q.correct_index
                        ? "text-emerald-700 font-medium before:content-['✓'] before:absolute before:left-0"
                        : "text-slate-600 before:content-['◦'] before:absolute before:left-0 before:text-slate-400"
                    }`}
                  >
                    {opt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </SectionCard>
      )}

      {/* 5. Prüf- & Wartungshinweise (nur wenn vorhanden, also nicht bei hazmat) */}
      {wartung && inspectionItems.length > 0 && (
        <SectionCard
          icon={Wrench}
          title="Prüf- & Wartungshinweise"
          subtitle="Nach BetrSichV / UVV"
          testId="welt-section-wartung"
        >
          <div className="space-y-2">
            {inspectionItems.map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 rounded-md border border-slate-200 p-3"
                data-testid={`welt-inspection-${idx}`}
              >
                <Badge
                  variant="outline"
                  className="bg-slate-100 border-slate-200 text-slate-700 text-[10px] uppercase tracking-wider shrink-0"
                >
                  {item.interval}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-900">{item.description}</p>
                  {item.who && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Durchführung: {item.who}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {wartung.next_check_recommended_in && (
            <p className="text-xs text-slate-500 mt-4">
              Empfohlene Sachkundigen-Prüfung:{" "}
              <span className="font-medium">
                {wartung.next_check_recommended_in}
              </span>
            </p>
          )}
        </SectionCard>
      )}

      {/* 6. Wiedervorlage-Zyklen */}
      <SectionCard
        icon={RotateCw}
        title="Wiedervorlage-Zyklen"
        subtitle="Pro Baustein – automatisch vom System überwacht"
        testId="welt-section-recurrence"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-700">Unterweisung</span>
            <Badge
              variant="outline"
              className="bg-slate-100 border-slate-200 text-slate-800 text-xs"
              data-testid="welt-recurrence-unterweisung"
            >
              {RECURRENCE_LABEL[course.recurrence] || course.recurrence}
            </Badge>
          </div>
          {course.gbu_recurrence && (
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-700">
                Gefährdungsbeurteilung – Review
              </span>
              <Badge
                variant="outline"
                className="bg-slate-100 border-slate-200 text-slate-800 text-xs"
                data-testid="welt-recurrence-gbu"
              >
                {RECURRENCE_LABEL[course.gbu_recurrence] ||
                  course.gbu_recurrence}
              </Badge>
            </div>
          )}
          {course.wartung_recurrence && (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-700">Wartung / Prüfung</span>
              <Badge
                variant="outline"
                className="bg-slate-100 border-slate-200 text-slate-800 text-xs"
                data-testid="welt-recurrence-wartung"
              >
                {RECURRENCE_LABEL[course.wartung_recurrence] ||
                  course.wartung_recurrence}
              </Badge>
            </div>
          )}
        </div>
        {course.ai_recurrence_reason && (
          <p
            className="text-xs text-slate-500 italic mt-4"
            data-testid="welt-recurrence-reason"
          >
            KI-Begründung: {course.ai_recurrence_reason}
          </p>
        )}
      </SectionCard>

      {/* Einzelkauf-Anhangseiten (Juni 2026): NUR im Chromium-Druck — je eine
          eigene A4-Seite, IMMER angehängt (keine Abfrage, kein Aufpreis). */}
      {druckansicht && (
        <>
          <DruckNachweisSeite course={course} firmenname={druckFirmenname} />
          <DruckWirksamkeitsChecklisteSeite course={course} />
        </>
      )}

      {/* V5I-B: Zwei-Türen-Popup — öffnet im anonymen Vorschau-Flow ausschließlich
          nach Klick auf „Dokumentation freigeben" (V5G §2). */}
      {!druckansicht && istOeffentlicheVorschau && (
        <ZweiTuerenPopup open={zweiTuerenOffen} course={course} />
      )}

      {/* Footer-Aktion: Freigeben oder Zuweisen.
          PHASE C2 (2026-06) — Mobile Entsticky (Variante C): unterhalb lg liegt
          dieser Status-/Freigabebereich NORMAL im Dokumentenfluss am Weltende
          (kein `sticky bottom-0`, keine Viewport-Heftung) → die Dokumentation ist
          mobil über die volle Höhe lesbar. Ab lg bleibt das bewährte Sticky-
          Verhalten (`lg:sticky lg:bottom-0`) erhalten. Mobile Bottom-Clearance
          (4rem + iOS Safe-Area) hält den rechtsbündigen Button frei von Home-
          Indicator/Safe-Area und dem Bottom-Right-Overlay ("Made with Emergent").
          STUFE 6: Im anonymen Vorschau-Flow bleibt nur die Readiness-Anzeige;
          der komplette Freigabe-/Aktionsbereich ist ausgeblendet (wird später
          durch den Zwei-Türen-Flow ersetzt). */}
      {!druckansicht && (!istOeffentlicheVorschau || !course.released) && (
      <div data-testid="welt-status-footer" className="lg:sticky lg:bottom-0 -mx-4 lg:-mx-10 bg-white/95 backdrop-blur border-t border-slate-200 px-4 lg:px-10 pt-4 pb-[calc(4rem_+_env(safe-area-inset-bottom))] lg:pb-4">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          {/* P0-D3 (2026-07-16): Readiness-Ampel — reine Anzeige der Backend-
              Wahrheit aus /api/courses/{id}/release-readiness. Farben AUSSCHLIESSLICH
              aus `status`; alle Backendtexte werden vollständig gezeigt. Keine
              eigene Frontend-Vollständigkeitslogik. */}
          {!course.released && (
            <ReadinessAmpel
              readiness={readiness}
              loading={readinessLoading}
              error={readinessError}
              onRefresh={() => loadReadiness(course.id)}
            />
          )}
          {/* V5K (auf Basis V5I-B/V5G §2): Freigabebereich — Sichtbarkeit
              ausschließlich aus dem bestehenden Fertigstellungszustand;
              Klick öffnet das bestehende Zwei-Türen-Popup. Text eingefroren
              per Betreiberentscheidung V5K. */}
          {istOeffentlicheVorschau && zeigeDokumentationFreigeben && (
            <div
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
              data-testid="freigabebereich"
            >
              <h2
                className="text-base md:text-lg font-semibold text-slate-900"
                data-testid="freigabebereich-ueberschrift"
              >
                Alles geprüft?
              </h2>
              <div className="mt-2 space-y-1 text-sm text-slate-600" data-testid="freigabebereich-text">
                <p>Ihre Arbeitsschutz-Dokumentation ist fertig erstellt.</p>
                <p>Prüfen Sie alle Inhalte in Ruhe und nehmen Sie bei Bedarf noch Änderungen vor.</p>
                <p>Sobald Sie mit dem Ergebnis zufrieden sind, können Sie Ihre Dokumentation freigeben.</p>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => {
                    // UI-V2C (Feb 2026): echter Freigabepfad — der bestehende
                    // Content-Builder liefert die read-only Vorschau, die von
                    // `useAbschlussKauffluss` unter dem eingefrorenen
                    // sessionStorage-Schlüssel „clartext_abschluss_content"
                    // erwartet wird (JSON-Objekt, non-empty). Danach Navigation
                    // via React-Router auf die neue Abschlussseite `/abschluss-v2`.
                    // Der alte `ZweiTuerenPopup` wird durch diesen Klick NICHT
                    // mehr geöffnet; sein Mount und State (`zweiTuerenOffen`)
                    // bleiben als Legacy im Repository unverändert erhalten.
                    const content = buildVorschauContent(course);
                    if (!content) {
                      toast.error(
                        "Freigabe nicht möglich: Dokumentinhalt konnte nicht erstellt werden.",
                      );
                      return;
                    }
                    try {
                      sessionStorage.setItem(
                        "clartext_abschluss_content",
                        JSON.stringify(content),
                      );
                      // Roher Tätigkeitsbegriff für den Trial-CTA der
                      // Abschlussseite (Fallback dort: content.title).
                      sessionStorage.setItem(
                        "clartext_abschluss_topic",
                        (course.ai_input_term || "").trim(),
                      );
                    } catch {
                      toast.error(
                        "Freigabe nicht möglich: Ihr Browser speichert die Sitzung nicht.",
                      );
                      return;
                    }
                    navigate("/abschluss-v2");
                  }}
                  className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-5"
                  data-testid="dokumentation-freigeben-button"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Dokumentation freigeben
                </Button>
              </div>
            </div>
          )}
          {!istOeffentlicheVorschau && (
          <div className="flex flex-wrap items-center justify-end gap-3">
          {!course.released ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setDiscardOpen(true)}
                data-testid="welt-discard-button"
                className="mr-auto text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Verwerfen
              </Button>
              <p className="text-sm text-slate-600 hidden md:block">
                Bereit zur Freigabe? Dies erzeugt einen Welt-Freigabe-Nachweis
                in der Prüfmappe.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate("/unternehmer/unterweisungen")}
                data-testid="welt-cancel-button"
              >
                Später
              </Button>
              <Button
                onClick={handleRelease}
                disabled={
                  releasing ||
                  readinessLoading ||
                  readiness?.status === "running" ||
                  readiness?.status === "blocked"
                }
                className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-5"
                data-testid="welt-release-button"
              >
                {releasing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird freigegeben…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Welt freigeben
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setAssignOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-5"
              data-testid="welt-assign-button"
              disabled={!!course.archived}
              title={
                course.archived
                  ? "Archivierte Welten können nicht mehr zugewiesen werden."
                  : undefined
              }
            >
              Unterweisung zuweisen
            </Button>
          )}
          </div>
          )}
        </div>
      </div>
      )}

      <AssignDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        courseId={course.id}
        courseTitle={course.title}
        courseRecurrence={course.recurrence}
        courseRecurrenceReason={course.ai_recurrence_reason}
        courseEinsatzbereichIds={course.einsatzbereich_ids || []}
        onAssigned={(res) => {
          // Sprint C: Toast nur als Kurz-Status; das detaillierte Ergebnis
          // erscheint direkt im Dialog.
          const created =
            (res?.summary?.ASSIGNMENT_CREATED || 0) +
            (res?.summary?.ASSIGNMENT_CREATED_NEW_VERSION || 0) +
            (res?.summary?.ASSIGNMENT_CREATED_INTERVAL_EXPIRED || 0) +
            (res?.summary?.ASSIGNMENT_REPLACED_BY_NEW_VERSION || 0);
          const skipped =
            (res?.summary?.ASSIGNMENT_SKIPPED_ALREADY_EXISTS || 0) +
            (res?.summary?.ASSIGNMENT_SKIPPED_VALID_PROOF || 0);
          if (created > 0 && skipped === 0) {
            toast.success(
              created === 1
                ? "Aufgabe zugewiesen."
                : `${created} Aufgaben zugewiesen.`
            );
          } else if (created === 0 && skipped > 0) {
            toast.message(
              `Keine neue Aufgabe nötig – ${skipped} bereits dokumentiert.`
            );
          } else {
            toast.success(
              `${created} neu, ${skipped} bereits vorhanden.`
            );
          }
        }}
      />

      <WeltEinsatzbereichDialog
        key={`welt-eb-${course.id}-${course.einsatzbereich_ids?.join(",") || ""}`}
        open={ebDialogOpen}
        onOpenChange={setEbDialogOpen}
        course={course}
        allEinsatzbereiche={einsatzbereiche}
        onSaved={(updatedCourse) => {
          setCourse(updatedCourse);
        }}
      />

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent data-testid="welt-discard-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Welt wirklich verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>
              {"\u201E"}
              {course.title}
              {"\u201C"} wird unwiderruflich gelöscht. Alle generierten
              Bausteine (Gefährdungsbeurteilung, Betriebsanweisung,
              Verständnisfragen, Wartungshinweise) gehen verloren. Sie können
              die Welt danach jederzeit neu erstellen lassen, müssen aber alle
              manuellen Anpassungen erneut machen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="welt-discard-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              disabled={discarding}
              data-testid="welt-discard-confirm-button"
              className="bg-red-600 hover:bg-red-700"
            >
              {discarding ? "Wird verworfen…" : "Endgültig verwerfen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* WELT-ARCHIVIERUNG (Feb 2026, F1–F5) — Confirm-Dialog mit konkreten
          Zahlen aus F5. Reversibel via „Reaktivieren"-Button im Banner. */}
      <AlertDialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <AlertDialogContent data-testid="welt-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Welt wirklich archivieren?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                <p>
                  {"\u201E"}
                  {course.title}
                  {"\u201C"} wird aus der Bibliothek, dem Mitarbeiter-
                  Nachschlagewerk und dem Dashboard-Zähler ausgeblendet. Neue
                  Zuweisungen sind gesperrt.
                </p>
                <div
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                  data-testid="welt-archive-confirm-stats"
                >
                  {archiveAssignmentStats === null ? (
                    <span className="inline-flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Zuweisungen werden geprüft…
                    </span>
                  ) : (
                    <ul className="text-xs space-y-1">
                      <li>
                        <strong data-testid="archive-stats-offen">
                          {archiveAssignmentStats.offen ?? "?"}
                        </strong>{" "}
                        offene Aufgaben bleiben bestehen, aber sind nicht mehr
                        erledigbar.
                      </li>
                      <li>
                        <strong data-testid="archive-stats-ueberfaellig">
                          {archiveAssignmentStats.ueberfaellig ?? "?"}
                        </strong>{" "}
                        überfällige Aufgaben werden weiter im Dashboard
                        gezählt, bis sie manuell bereinigt werden.
                      </li>
                      <li>
                        <strong data-testid="archive-stats-erledigt">
                          {archiveAssignmentStats.erledigt ?? "?"}
                        </strong>{" "}
                        erledigte Nachweise bleiben in der Prüfmappe
                        vollständig erhalten (WORM).
                      </li>
                    </ul>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Reversibel: Sie können die Welt jederzeit über
                  {" \u201E"}Reaktivieren{"\u201C"} wieder freischalten.
                  Bestehende Nachweise bleiben unverändert.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="welt-archive-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveToggle}
              disabled={archiveLoading}
              data-testid="welt-archive-confirm-button"
              className="bg-slate-900 hover:bg-slate-800"
            >
              {archiveLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird archiviert…
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Archivieren
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sprint C – Versions-Bump-Dialog (Feb 2026) */}
      <Dialog open={newVersionOpen} onOpenChange={(o) => {
        if (!o) closeNewVersion();
      }}>
        <DialogContent
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="new-version-dialog"
        >
          {!newVersionResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  Als neue Version freigeben
                </DialogTitle>
                <DialogDescription>
                  Fachliche Änderungen werden als neue Version markiert.
                  Bestehende offene Aufgaben mit der alten Version werden
                  automatisch durch die neue ersetzt – betroffene Mitarbeiter
                  sehen sofort die aktuelle Welt.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  <strong className="font-medium">Wichtig:</strong> Reine
                  Tippfehler oder Format-Korrekturen brauchen{" "}
                  <em>keine</em> neue Version. Nutze diesen Knopf nur, wenn
                  sich der fachliche Inhalt geändert hat.
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Titel
                  </label>
                  <Input
                    value={nvTitleDraft}
                    onChange={(e) => setNvTitleDraft(e.target.value)}
                    data-testid="new-version-title"
                    placeholder="Titel der Welt"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Kurzbeschreibung
                  </label>
                  <Textarea
                    value={nvDescDraft}
                    onChange={(e) => setNvDescDraft(e.target.value)}
                    data-testid="new-version-desc"
                    rows={3}
                    placeholder="Kurze fachliche Beschreibung"
                  />
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                  Neue Version:{" "}
                  <strong className="font-mono text-slate-900">
                    v{course.version || "1.0"}
                  </strong>{" "}
                  → ?{" "}
                  <span className="text-slate-500">
                    (wird automatisch hochgezählt)
                  </span>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={closeNewVersion}
                  data-testid="new-version-cancel"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={submitNewVersion}
                  disabled={nvSubmitting}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="new-version-submit"
                >
                  {nvSubmitting
                    ? "Wird freigegeben…"
                    : "Als neue Version freigeben"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle
                  className="font-heading text-xl"
                  data-testid="new-version-result-title"
                >
                  Version v{newVersionResult.new_version} freigegeben
                </DialogTitle>
                <DialogDescription>
                  Die neue Version ist ab sofort für alle künftigen
                  Zuweisungen aktiv. Bestehende offene Aufgaben mit
                  v{newVersionResult.old_version} wurden automatisch
                  ersetzt.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">
                      Alte Version
                    </div>
                    <div className="font-heading text-lg font-bold text-slate-900 mt-0.5 font-mono">
                      v{newVersionResult.old_version}
                    </div>
                  </div>
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-medium">
                      Neue Version
                    </div>
                    <div
                      className="font-heading text-lg font-bold text-emerald-900 mt-0.5 font-mono"
                      data-testid="new-version-result-version"
                    >
                      v{newVersionResult.new_version}
                    </div>
                  </div>
                </div>
                <div
                  className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900"
                  data-testid="new-version-result-affected"
                >
                  <strong className="font-medium">
                    {newVersionResult.affected_employees}
                  </strong>{" "}
                  {newVersionResult.affected_employees === 1
                    ? "Mitarbeiter"
                    : "Mitarbeiter"}{" "}
                  hatte
                  {newVersionResult.affected_employees === 1 ? "" : "n"}{" "}
                  noch eine offene Aufgabe der alten Version – diese{" "}
                  {newVersionResult.affected_employees === 1
                    ? "wurde"
                    : "wurden"}{" "}
                  automatisch ersetzt.
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={closeNewVersion}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="new-version-result-close"
                >
                  Schließen
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 3 — Einsatzbereich-Verteilung der Welt
// ---------------------------------------------------------------------------

function WeltEinsatzbereichRow({ course, allEinsatzbereiche, onEdit }) {
  const ebs = useMemo(() => {
    const ids = course.einsatzbereich_ids || [];
    return ids
      .map((id) => allEinsatzbereiche.find((e) => e.id === id))
      .filter(Boolean);
  }, [course.einsatzbereich_ids, allEinsatzbereiche]);

  return (
    <div
      className="mt-4 flex items-center gap-2 flex-wrap"
      data-testid="welt-einsatzbereiche-row"
    >
      <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">
        Verteilung
      </span>
      {ebs.length === 0 ? (
        <span className="text-sm text-slate-500 italic">
          Keine Einsatzbereiche – manuelle Zuweisung
        </span>
      ) : (
        ebs.map((eb) => (
          <Badge
            key={eb.id}
            variant="outline"
            className={`text-xs ${eb.archived ? "bg-slate-100 border-slate-200 text-slate-500 line-through decoration-slate-300" : "bg-indigo-50 border-indigo-200 text-indigo-800"}`}
            data-testid={`welt-einsatzbereich-pill-${eb.id}`}
          >
            <MapPin className="w-3 h-3 mr-1" />
            {eb.name}
          </Badge>
        ))
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={onEdit}
        data-testid="welt-einsatzbereich-edit-button"
        className="h-7 px-2 text-slate-500 hover:text-slate-900"
      >
        <Pencil className="w-3 h-3 mr-1" />
        Bearbeiten
      </Button>
    </div>
  );
}

export function WeltEinsatzbereichDialog({
  open,
  onOpenChange,
  course,
  allEinsatzbereiche,
  onSaved,
}) {
  const [selected, setSelected] = useState(
    () => new Set(course?.einsatzbereich_ids || [])
  );
  const [saving, setSaving] = useState(false);

  const active = useMemo(
    () => allEinsatzbereiche.filter((e) => !e.archived),
    [allEinsatzbereiche]
  );

  // Aktuell gespeicherte aber archivierte IDs trotzdem mit anzeigen
  // (Read-only — kann nur entfernt werden, nicht hinzugefügt).
  const visible = useMemo(() => {
    const m = new Map(active.map((e) => [e.id, e]));
    for (const id of selected) {
      if (!m.has(id)) {
        const fromAll = allEinsatzbereiche.find((e) => e.id === id);
        if (fromAll) m.set(id, fromAll);
      }
    }
    return Array.from(m.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "de")
    );
  }, [active, allEinsatzbereiche, selected]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!course) return;
    setSaving(true);
    try {
      const updated = await api.updateCourseEinsatzbereiche(
        course.id,
        Array.from(selected)
      );
      toast.success("Einsatzbereiche der Welt aktualisiert.");
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[85vh] overflow-y-auto"
        data-testid="welt-einsatzbereich-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Einsatzbereiche dieser Welt
          </DialogTitle>
          <DialogDescription>
            Wer arbeitet mit {"\u201E"}
            {course?.title}
            {"\u201C"}? Markierte Bereiche werden bei der nächsten Zuweisung
            automatisch vorausgewählt.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {visible.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              Noch keine Einsatzbereiche in den Einstellungen angelegt.
            </p>
          ) : (
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
              {visible.map((eb) => {
                const checked = selected.has(eb.id);
                return (
                  <label
                    key={eb.id}
                    htmlFor={`welt-eb-${eb.id}`}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${eb.archived ? "opacity-60" : "hover:bg-slate-50"}`}
                  >
                    <Checkbox
                      id={`welt-eb-${eb.id}`}
                      checked={checked}
                      onCheckedChange={() => toggle(eb.id)}
                      disabled={eb.archived && !checked}
                      data-testid={`welt-eb-checkbox-${eb.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">
                        {eb.name}
                        {eb.archived && (
                          <Badge
                            variant="outline"
                            className="ml-2 bg-slate-100 border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider"
                          >
                            Archiviert
                          </Badge>
                        )}
                      </div>
                      {eb.description && (
                        <div className="text-xs text-slate-500 truncate">
                          {eb.description}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">
            Leere Auswahl = Welt wird manuell zugewiesen, keine automatische
            Vorauswahl.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="welt-eb-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="welt-eb-save"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {saving ? "Speichert…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
